import { useState } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CreateOrEditUserModal from "modals/CreateOrEditUserModal/CreateOrEditUserModal";
import { ROLES, User } from "types/user";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import { Add } from "@mui/icons-material";
import { MainLayoutContainer } from "components/Layout";
import { Stack, Tab, Tabs } from "@mui/material";
import { ResponsiveButton, ResponsiveButtonLink } from "components/Button";
import { AdminRoutes } from "config/routes";
import { useUserList } from "./useUserList";
import { TabPanel } from "components/TabPanel";
import { UserListAdminMenu, UserTable } from "./components";
import { ProvisionCognitoUsersModal } from "modals/ProvisionCognitoUsersModal";
import { SyncCognitoAttributesModal } from "modals/SyncCognitoAttributesModal/SyncCognitoAttributesModal";
import { AccountGroupsPanel } from "./groups/AccountGroupsPanel";

export type ActionButton = {
  title: string;
  icon: any;
  handleClick: () => void;
  color:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
};

export function UserList() {
  const {
    loading,
    error,
    users,
    handleActionClick,
    handleDelete,
    createOrEditUserModal,
    handleCreateOrUpdateUser,
    isEditing,
    userToEdit,
    deleteUserModal,
    setDeleteUserModal,
    userToDelete,
    handleCreateOrUpdateUserModalOpen,
    handleCreateUserModalDismiss,
    userCounter,
    inviteUserModal,
    setInviteUserModal,
    sendInvitation,
    provisionCognitoModal,
    setProvisionCognitoModal,
    syncCognitoAttributesModal,
    setSyncCognitoAttributesModal,
  } = useUserList();

  const [value, setValue] = useState(0);

  const handleChange = (_: any, newValue: number) => {
    setValue(newValue);
  };

  const allowedRoles = [
    ROLES.ADMIN,
    ROLES.DIRECTOR,
    ROLES.MANAGER,
    ROLES.COACH,
    ROLES.STUDENT,
  ];

  return (
    <MainLayoutContainer
      title="Users"
      loading={loading}
      error={error}
      buttonActions={
        <Stack direction="row" spacing={2} alignItems="center">
          <ResponsiveButtonLink
            variant="outlined"
            icon={<FileUploadIcon />}
            to={AdminRoutes.UploadCSVUsers}
          >
            Upload CSV File
          </ResponsiveButtonLink>
          <ResponsiveButton
            onClick={() => handleCreateOrUpdateUserModalOpen()}
            icon={<Add />}
          >
            New User
          </ResponsiveButton>
          <UserListAdminMenu
            onAddDbUsersToCognito={() => setProvisionCognitoModal(true)}
            onSyncCognitoAttributes={() => setSyncCognitoAttributesModal(true)}
          />
        </Stack>
      }
    >
      <>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Users"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label={`All users (${userCounter.total})`}
            id="users-tab-0"
            aria-controls="users-tabpanel-0"
          />
          <Tab
            label={`Admins (${userCounter.admins})`}
            id="users-tab-1"
            aria-controls="users-tabpanel-1"
          />
          <Tab
            label={`Managers (${userCounter.managers})`}
            id="users-tab-2"
            aria-controls="users-tabpanel-2"
          />
          <Tab
            label={`Directors (${userCounter.directors})`}
            id="users-tab-3"
            aria-controls="users-tabpanel-3"
          />
          <Tab
            label={`Coaches (${userCounter.coaches})`}
            id="users-tab-4"
            aria-controls="users-tabpanel-4"
          />
          <Tab
            label={`Learners (${userCounter.students})`}
            id="users-tab-5"
            aria-controls="users-tabpanel-5"
          />
          <Tab
            label="Groups"
            id="users-tab-6"
            aria-controls="users-tabpanel-6"
          />
        </Tabs>

        <TabPanel value={value} index={0}>
          <UserTable users={users} handleActionClick={handleActionClick} />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <UserTable
            users={users}
            handleActionClick={handleActionClick}
            role={ROLES.ADMIN}
          />
        </TabPanel>

        <TabPanel value={value} index={2}>
          <UserTable
            users={users}
            handleActionClick={handleActionClick}
            role={ROLES.MANAGER}
          />
        </TabPanel>

        <TabPanel value={value} index={3}>
          <UserTable
            users={users}
            handleActionClick={handleActionClick}
            role={ROLES.DIRECTOR}
          />
        </TabPanel>

        <TabPanel value={value} index={4}>
          <UserTable
            users={users}
            handleActionClick={handleActionClick}
            role={ROLES.COACH}
          />
        </TabPanel>

        <TabPanel value={value} index={5}>
          <UserTable
            users={users}
            handleActionClick={handleActionClick}
            role={ROLES.STUDENT}
          />
        </TabPanel>

        <TabPanel value={value} index={6}>
          <AccountGroupsPanel />
        </TabPanel>

        {createOrEditUserModal && (
          <CreateOrEditUserModal
            allowedRoles={allowedRoles}
            handleCreateOrEditUserChange={handleCreateOrUpdateUser}
            createUserModal={createOrEditUserModal}
            handleCreateUserModalDismiss={handleCreateUserModalDismiss}
            userToEdit={userToEdit}
            isEditing={isEditing}
          />
        )}
        {deleteUserModal && (
          <ConfirmActionModal
            title={"Heads up!"}
            message={"Are you sure you want to delete this user?"}
            show={deleteUserModal}
            onConfirm={() => handleDelete(userToDelete as User)}
            onDismiss={() => {
              setDeleteUserModal(false);
            }}
          />
        )}
        {inviteUserModal && userToEdit && (
          <ConfirmActionModal
            title={"Invite User"}
            message={`A formal email invitation will be sent to the email address ${userToEdit?.email} `}
            show={inviteUserModal}
            onConfirm={() => sendInvitation(userToEdit?.email ?? "")}
            onDismiss={() => {
              setInviteUserModal(false);
            }}
          />
        )}
        <ProvisionCognitoUsersModal
          open={provisionCognitoModal}
          onClose={() => setProvisionCognitoModal(false)}
        />
        <SyncCognitoAttributesModal
          open={syncCognitoAttributesModal}
          onClose={() => setSyncCognitoAttributesModal(false)}
        />
      </>
    </MainLayoutContainer>
  );
}
